import { z } from 'zod';

const CustomerInfoSchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  company_name: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().nullable(),
  postal_code: z.string().nullable(),
  postal_code4: z.string().nullable(),
  non_us_tax_exemption_number: z.string().nullable(),
  us_tax_exemption_number: z.string().nullable(),
  tax_exemption_agreement: z.boolean(),
  email: z.string(),
  phone: z.string().nullable(),
  billing_letter_option: z.string().nullable(),
  tax_exemption_expiration: z.string().nullable(),

  has_stripe_customer: z.boolean().optional(),
});

const CreditCardInfoSchema = z
  .object({
    credit_card_type: z.string(),
    card_token: z.string(),
    masked_credit_card_number: z.string(),
    expiration_date: z.string(),
    data: z.string().nullish(),
    digital_wallet: z.string().nullable(),
  })
  .nullable();

const PaypalInfoSchema = z
  .object({
    agreement_id: z.string(),
  })
  .nullable();

const EcheckInfoSchema = z
  .object({
    echeckToken: z.string(),
    account_type: z.string(),
    account_number: z.string(),
    account_number_confirmation: z.string().nullable(),
    routing_number: z.string(),
    masked_account_number: z.string(),
    echeck_token: z.string(),
  })
  .nullable();

const AmazonPayInfoSchema = z
  .object({
    token: z.string(),
  })
  .nullable();

const PaymentInfoSchema = z.object({
  payment_type: z.string().nullable(),
  credit_card_info: CreditCardInfoSchema,
  echeck_info: EcheckInfoSchema,
  check: z.unknown().nullable(),
  paypal: PaypalInfoSchema,
  amazonpay: AmazonPayInfoSchema,
});

const AccountEmailAddressesSchema = z.object({
  all_account_email_addresses: z.array(z.string()),
  contact_email_address: z.string(),
});

const OverviewSchema = z.object({
  prepay: z.boolean(),
  account_email_addresses: AccountEmailAddressesSchema,
  customer_info_on_file: z.boolean(),
  monthly_fee: z.string(),
  payment_method_on_file: z.boolean(),
  trial_end_date: z.string().nullable(),
  echeck_allowed: z.boolean(),
  next_charge_scheduled: z.string().nullable(),
  days_left_in_period: z.number().nullable(),
  contact_tier: z.string().nullable(),
  account_status: z.string(),
  payment_period: z.number().nullable(),
  balance: z.string(),
  prepay_balance: z.string().nullable(),
  projected_prepay_balance: z.string().nullable(),
  projected_prepay_renewal: z.boolean(),
  months_remaining: z.number().nullable(),
  prepay_next_charge_scheduled: z.string().nullable(),
});

export const BillingInfoResponseSchema = z.object({
  customer_info: CustomerInfoSchema,
  payment_info: PaymentInfoSchema,
  overview: OverviewSchema,
});

// request
const CustomerInfoRequestSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  address1: z.string(),
  address2: z.string(),
  company_name: z.string(),
  city: z.string(),
  state: z.string(),
  country: z.string(),
  postal_code: z.string(),
  postal_code4: z.string(),
  email: z.string(),
  phone: z.string(),
});

const CreditCardInfoRequestSchema = z
  .object({
    credit_card_type: z.string(),
    card_token: z.string(),
  })
  .nullable();

const PaymentInfoRequestSchema = z.object({
  payment_type: z.string().nullable(),
  credit_card_info: CreditCardInfoRequestSchema,
  echeck_info: EcheckInfoSchema,
  check: z.unknown().nullable(),
  paypal: PaypalInfoSchema,
  amazonpay: AmazonPayInfoSchema,
  stripe: z.object({ token: z.string() }).nullish(),
});

export const BillingInfoRequestSchema = z.object({
  customer_info: CustomerInfoRequestSchema,
  payment_info: PaymentInfoRequestSchema.nullish(),
});

// TypeScript Interfaces
export type CustomerInfo = z.infer<typeof CustomerInfoSchema>;

export type CreditCardInfo = z.infer<typeof CreditCardInfoSchema>;

export type PaymentInfo = z.infer<typeof PaymentInfoSchema>;

export type AccountEmailAddresses = z.infer<typeof AccountEmailAddressesSchema>;

export type Overview = z.infer<typeof OverviewSchema>;

export type BillingInfoResponse = z.infer<typeof BillingInfoResponseSchema>;

export type BillingInfoRequest = z.infer<typeof BillingInfoRequestSchema>;
